// =============================================================================
// Optim TURPE v2 — fixtures mock-first (DEV-715 / DEV-783).
// Reflètent le payload courtage réel {actuelle, meilleure, diagnostic, restitution}
// APRÈS DEV-762/#117 : diagnostic.plages porte `stats_kw` (pas `stats`), un `driver`
// enrichi (max_kw, *_proche_du_souscrit, mois_concernes_depassement…), `synthese`
// = {plage_pilote, message}, et la qualité vit dans `restitution.qualite_donnees`
// (+ `reserves`). Servent Storybook + le store en mode mock. Cas couverts :
// C4 économie (pic isolé), C2 économie (5 plages, active), C2 sur-dimensionné sans
// gain (état sur_segmente), C2 économie + sur-dimensionné (METHA — l'économie prime),
// C4 déjà optimal, C5 puissance unique.
// =============================================================================

import { optimTurpeV2ServiEtudes } from "@modules/courtage/stores/optim-turpe-v2.servi.mock-data"
import type {
    OptimTurpeV2DriverType,
    OptimTurpeV2Etude,
    OptimTurpeV2PlageDiagnostic,
} from "@modules/courtage/types/optim-turpe-v2"

/**
 * Fabrique un diagnostic de plage au format réel (DEV-783). `stats_kw` porte les
 * percentiles mesurés ; le `driver` qualifie le profil (normal / pic isolé / saisonnier).
 */
function plage(
    souscrit_propose_kva: number,
    stats: { p50: number; p95: number; p99: number; max: number },
    type: OptimTurpeV2DriverType,
    opts: { mois?: number[]; hiverPct?: number } = {}
): OptimTurpeV2PlageDiagnostic {
    return {
        souscrit_propose_kva,
        n_points: 20640,
        duree_h: 3440,
        stats_kw: { ...stats, moyenne: Math.round(stats.p50 * 0.92) },
        driver: {
            type,
            max_kw: stats.max,
            ecart_max_sur_p99_pct:
                stats.p99 > 0
                    ? Math.round(((stats.max - stats.p99) / stats.p99) * 1000) /
                      10
                    : null,
            points_proches_du_souscrit: type === "pic_isole" ? 12 : 720,
            duree_proche_du_souscrit_h: type === "pic_isole" ? 2 : 120,
            mois_concernes_depassement: opts.mois ?? [6, 7, 8],
            part_hiver_depassement_pct: opts.hiverPct ?? 0,
        },
        arbitrage: null,
    }
}

/** C4 — économie identifiée, dénivelé tiré par un pic isolé (cas vitrine). */
export const optimTurpeV2MockC4: OptimTurpeV2Etude = {
    _id: "mock-c4-0001",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-16T09:00:00.000Z",
    source: { idsource: "50054931846098" },
    compteur: {
        segment: "C4",
        raccordement: { puissance_raccordement_soutirage: 250 },
    },
    inputs: {
        source: { _id_source: "mock-c4-source" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
        optimisation: { allow_cmdps: true, marge_securite_pct: 5 },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPCU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
            },
        },
        couts: {
            turpe: 6120,
            turpeCSF: 4200,
            turpeCSV: 1800,
            turpeCER: 120,
            turpeCMDPS: 0,
        },
    },
    meilleure: {
        structure_tarifaire: {
            // FTA recommandée ≠ actuelle : le moteur optimise la FTA avec le dénivelé.
            formule_tarifaire_acheminement: { fta: "BTSUPLU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 60, HCH: 66, HPB: 74, HCB: 86 },
            },
        },
        couts: {
            turpe: 4790,
            turpeCSF: 2950,
            turpeCSV: 1720,
            turpeCER: 120,
            turpeCMDPS: 90,
        },
        variations: {
            variation_absolue_turpe: -1330,
            variation_relative_turpe: -0.217,
        },
    },
    diagnostic: {
        sens: "soutirage",
        grandeur: "apparente",
        plages: {
            HPH: plage(
                60,
                { p50: 48, p95: 57, p99: 60, max: 62 },
                "comportement_normal",
                { mois: [1, 2, 11, 12], hiverPct: 82 }
            ),
            HCH: plage(
                66,
                { p50: 52, p95: 63, p99: 66, max: 67 },
                "comportement_normal",
                { mois: [1, 2, 12], hiverPct: 78 }
            ),
            HPB: plage(
                74,
                { p50: 58, p95: 71, p99: 74, max: 75 },
                "comportement_normal",
                { mois: [5, 6, 7, 8, 9], hiverPct: 10 }
            ),
            HCB: plage(
                86,
                { p50: 67, p95: 78, p99: 86, max: 108 },
                "pic_isole",
                { mois: [8], hiverPct: 0 }
            ),
        },
        synthese: {
            plage_pilote: "HCB",
            plages_tirees_par_pic: ["HCB"],
            arbitrage_advanced_pertinent: false,
            message:
                "Le dénivelé HCB est tiré par un pic isolé d'août — souscrire au comportement normal libère la CSF.",
        },
    },
    pointes_mesurees_kw: { HPH: 62, HCH: 67, HPB: 75, HCB: 108 },
    reserves: [
        {
            code: "donnees-extrapolees",
            label: "9 % de courbe de charge extrapolée (micro-trous comblés)",
        },
    ],
    qualite_donnees: {
        niveau: "reserve",
        couverture_globale_pct: 91,
        couverture_hiver_pct: 100,
        trou_max_h: 26,
        meilleure_periode: null,
    },
    // 2 registres jamais sommés : ferme = nette + cmdps.
    economie: {
        ferme_eur: 1420,
        cmdps_estimee_eur: 90,
        nette_eur: 1330,
        relative_pct: 21.7,
    },
    explain: {
        conclusion:
            "Passer en BTSUPLU4 et descendre le dénivelé (HCB 250 → 86 kVA) : ~1 420 €/an d'économie ferme.",
        pourquoi:
            "Le dénivelé HCB était tiré par un pic isolé du mois d'août (108 kVA, 3 h) — souscrire au comportement normal (~86 kVA) libère la CSF toute l'année, contre ~90 €/an de CMDPS à confirmer.",
        notes: "",
        generated: true,
    },
}

/** C2 — économie identifiée, dénivelé 5 plages, grandeur ACTIVE (kW). */
export const optimTurpeV2MockC2: OptimTurpeV2Etude = {
    _id: "mock-c2-0002",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-16T09:30:00.000Z",
    source: { idsource: "50099565837769" },
    compteur: {
        segment: "C2",
        raccordement: { puissance_raccordement_soutirage: 1200 },
    },
    inputs: {
        source: { _id_source: "mock-c2eco-source" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
        optimisation: { allow_cmdps: true, marge_securite_pct: 5 },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "HTALU5" },
            puissance_souscrite_soutirage: {
                denivele: { PTE: 600, HPH: 600, HCH: 600, HPB: 600, HCB: 600 },
            },
        },
        couts: {
            turpe: 42000,
            turpeCSF: 31000,
            turpeCSV: 10400,
            turpeCER: 600,
        },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "HTALU5" },
            puissance_souscrite_soutirage: {
                denivele: { PTE: 420, HPH: 470, HCH: 520, HPB: 540, HCB: 560 },
            },
        },
        couts: {
            turpe: 36500,
            turpeCSF: 25800,
            turpeCSV: 10100,
            turpeCMDPS: 320,
            turpeCER: 600,
        },
        variations: {
            variation_absolue_turpe: -5500,
            variation_relative_turpe: -0.131,
        },
    },
    diagnostic: {
        sens: "soutirage",
        grandeur: "active",
        plages: {
            PTE: plage(
                420,
                { p50: 360, p95: 405, p99: 418, max: 430 },
                "saisonnier",
                { mois: [12, 1, 2], hiverPct: 95 }
            ),
            HPH: plage(
                470,
                { p50: 410, p95: 458, p99: 470, max: 472 },
                "comportement_normal",
                { mois: [11, 12, 1, 2, 3], hiverPct: 80 }
            ),
            HCH: plage(
                520,
                { p50: 450, p95: 508, p99: 520, max: 523 },
                "comportement_normal",
                { mois: [12, 1, 2], hiverPct: 74 }
            ),
            HPB: plage(
                540,
                { p50: 470, p95: 528, p99: 540, max: 544 },
                "comportement_normal",
                { mois: [6, 7, 8, 9], hiverPct: 12 }
            ),
            HCB: plage(
                560,
                { p50: 480, p95: 545, p99: 560, max: 612 },
                "pic_isole",
                { mois: [7], hiverPct: 0 }
            ),
        },
        synthese: {
            plage_pilote: "HCB",
            plages_tirees_par_pic: ["HCB"],
            arbitrage_advanced_pertinent: false,
            message:
                "Comportement saisonnier marqué en PTE ; HCB porté par un pic isolé de juillet.",
        },
    },
    pointes_mesurees_kw: { PTE: 430, HPH: 472, HCH: 523, HPB: 544, HCB: 612 },
    qualite_donnees: {
        niveau: "fiable",
        couverture_globale_pct: 100,
        couverture_hiver_pct: 100,
        trou_max_h: 0,
        meilleure_periode: null,
    },
    economie: {
        ferme_eur: 5820,
        cmdps_estimee_eur: 320,
        nette_eur: 5500,
        relative_pct: 13.1,
    },
}

/**
 * C2 dont les appels mesurés sont très en dessous du dénivelé souscrit, sans gain.
 *
 * Cette fixture servait l'ancien état `sur_segmente`, supprimé avec le plancher de
 * 250 kW (A6) : elle vaut désormais comme cas « déjà optimale » dont le dénivelé
 * actuel est peu vraisemblable — ce que dit le contrôle servi (§4.5).
 */
export const optimTurpeV2MockC2SurSegmente: OptimTurpeV2Etude = {
    _id: "mock-c2-0001",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-16T09:10:00.000Z",
    source: { idsource: "30002430744268" },
    compteur: {
        segment: "C2",
        raccordement: { puissance_raccordement_soutirage: 510 },
    },
    inputs: {
        source: { _id_source: "mock-c2-source" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
        optimisation: { allow_cmdps: true },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "HTACU5" },
            puissance_souscrite_soutirage: {
                denivele: { PTE: 250, HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
            },
        },
        couts: { turpe: 9800, turpeCSF: 7200, turpeCSV: 2480, turpeCER: 120 },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "HTACU5" },
            puissance_souscrite_soutirage: {
                denivele: { PTE: 250, HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
            },
        },
        couts: { turpe: 9800, turpeCSF: 7200, turpeCSV: 2480, turpeCER: 120 },
        variations: { variation_absolue_turpe: 0, variation_relative_turpe: 0 },
    },
    pointes_mesurees_kw: { PTE: 120, HPH: 140, HCH: 150, HPB: 148, HCB: 153 },
    qualite_donnees: {
        niveau: "fiable",
        couverture_globale_pct: 99,
        couverture_hiver_pct: 100,
        trou_max_h: 4,
        meilleure_periode: null,
    },
    economie: {
        ferme_eur: 0,
        cmdps_estimee_eur: 0,
        nette_eur: 0,
        relative_pct: 0,
    },
}

/**
 * C2 économie + sur-dimensionné (METHA-like) — pointes < plancher C2 (250 kW) MAIS gain TURPE réel
 * via changement de FTA (HTALU5 → HTACU5). DEV-783 : l'économie PRIME (état economie), le
 * sur-dimensionnement est signalé en note complémentaire, jamais en écrasant le chiffre.
 */
export const optimTurpeV2MockC2EconomieSurDim: OptimTurpeV2Etude = {
    _id: "mock-c2-0003",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-19T09:00:00.000Z",
    source: { idsource: "50041049558503" },
    compteur: {
        segment: "C2",
        raccordement: { puissance_raccordement_soutirage: 250 },
    },
    inputs: {
        source: { _id_source: "mock-c2metha-source" },
        data: { date_from: "2025-06-19", date_to: "2026-06-19" },
        optimisation: { allow_cmdps: true, marge_securite_pct: 5 },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "HTALU5" },
            puissance_souscrite_soutirage: {
                denivele: { PTE: 250, HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
            },
        },
        couts: { turpe: 17250, turpeCSF: 8857, turpeCSV: 8273, turpeCER: 120 },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "HTACU5" },
            puissance_souscrite_soutirage: {
                denivele: { PTE: 158, HPH: 158, HCH: 165, HPB: 165, HCB: 165 },
            },
        },
        couts: { turpe: 12502, turpeCSF: 2384, turpeCSV: 9998, turpeCER: 120 },
        variations: {
            variation_absolue_turpe: -4748,
            variation_relative_turpe: -0.275,
        },
    },
    diagnostic: {
        sens: "soutirage",
        grandeur: "active",
        plages: {
            PTE: plage(
                158,
                { p50: 96, p95: 150, p99: 156, max: 158 },
                "saisonnier",
                { mois: [12, 1, 2], hiverPct: 88 }
            ),
            HPH: plage(
                158,
                { p50: 98, p95: 150, p99: 157, max: 158 },
                "comportement_normal",
                { mois: [11, 12, 1], hiverPct: 70 }
            ),
            HCH: plage(
                165,
                { p50: 100, p95: 158, p99: 164, max: 165 },
                "saisonnier",
                { mois: [12, 1, 2], hiverPct: 85 }
            ),
            HPB: plage(
                165,
                { p50: 101, p95: 158, p99: 164, max: 165 },
                "comportement_normal",
                { mois: [6, 7, 8], hiverPct: 5 }
            ),
            HCB: plage(
                165,
                { p50: 100, p95: 158, p99: 164, max: 165 },
                "comportement_normal",
                { mois: [6, 7, 8], hiverPct: 5 }
            ),
        },
        synthese: {
            plage_pilote: "HCB",
            plages_tirees_par_pic: [],
            arbitrage_advanced_pertinent: false,
            message:
                "Comportement soutenu sous le plancher C2 (250 kW) ; le gain vient du passage HTALU5 → HTACU5.",
        },
    },
    pointes_mesurees_kw: { PTE: 158, HPH: 158, HCH: 165, HPB: 165, HCB: 165 },
    reserves: [
        {
            code: "donnees-extrapolees",
            label: "11 % de courbe de charge extrapolée (couverture 89 %)",
        },
        {
            code: "reactif-absent",
            label: "Pas de puissance réactive dans les données de courbe de charge",
        },
    ],
    qualite_donnees: {
        niveau: "indicatif",
        couverture_globale_pct: 89,
        couverture_hiver_pct: 100,
        trou_max_h: 39,
        meilleure_periode: {
            date_from: "2025-05-12",
            date_to: "2026-05-11",
            pct_available: 100,
        },
    },
    economie: {
        ferme_eur: 4748,
        cmdps_estimee_eur: 0,
        nette_eur: 4748,
        relative_pct: 27.5,
    },
    explain: {
        conclusion:
            "Passage de HTALU5 vers HTACU5 avec alignement des dénivelés sur les puissances réellement mesurées : économie ferme de 4 748 € soit -27,5 % du TURPE total. Aucune CMDPS en jeu.",
        pourquoi:
            "Le gain provient quasi-intégralement de la CSF : en HTACU5, la souscription est facturée sur les puissances réelles par plage et non plus sur un dénivelé uniforme à 250 kW.",
        notes: "",
        generated: true,
    },
}

/** C4 — déjà optimal : la reco n'améliore pas (économie nette ≤ seuil), dit franchement. */
export const optimTurpeV2MockC4DejaOptimal: OptimTurpeV2Etude = {
    _id: "mock-c4-0002",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-18T09:00:00.000Z",
    source: { idsource: "30001450684853" },
    compteur: {
        segment: "C4",
        raccordement: { puissance_raccordement_soutirage: 120 },
    },
    inputs: {
        source: { _id_source: "mock-c4opt-source" },
        data: { date_from: "2025-06-19", date_to: "2026-06-19" },
        optimisation: { allow_cmdps: true, marge_securite_pct: 5 },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPCU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 60, HCH: 60, HPB: 60, HCB: 60 },
            },
        },
        couts: { turpe: 11200, turpeCSF: 6800, turpeCSV: 4280, turpeCER: 120 },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPCU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 56, HCH: 65, HPB: 65, HCB: 65 },
            },
        },
        couts: { turpe: 11257, turpeCSF: 6874, turpeCSV: 4263, turpeCER: 120 },
        variations: {
            variation_absolue_turpe: 57,
            variation_relative_turpe: 0.005,
        },
    },
    diagnostic: {
        sens: "soutirage",
        grandeur: "apparente",
        plages: {
            HPH: plage(
                56,
                { p50: 44, p95: 54, p99: 56, max: 56 },
                "saisonnier",
                { mois: [12, 1, 2], hiverPct: 90 }
            ),
            HCH: plage(
                65,
                { p50: 52, p95: 63, p99: 64, max: 64.5 },
                "comportement_normal",
                { mois: [6, 7, 8], hiverPct: 10 }
            ),
            HPB: plage(
                65,
                { p50: 53, p95: 63, p99: 64, max: 64.8 },
                "comportement_normal",
                { mois: [6, 7, 8], hiverPct: 8 }
            ),
            HCB: plage(
                65,
                { p50: 52, p95: 63, p99: 64, max: 64.6 },
                "comportement_normal",
                { mois: [6, 7, 8], hiverPct: 8 }
            ),
        },
        synthese: {
            plage_pilote: "HCB",
            plages_tirees_par_pic: [],
            arbitrage_advanced_pertinent: false,
            message:
                "La structure actuelle est déjà quasi-optimale : la reco augmenterait légèrement le forfait.",
        },
    },
    pointes_mesurees_kw: { HPH: 56, HCH: 64.5, HPB: 64.8, HCB: 64.6 },
    reserves: [
        {
            code: "donnees-extrapolees",
            label: "Courbe de charge complète à 99 % (micro-trous comblés)",
        },
    ],
    qualite_donnees: {
        niveau: "fiable",
        couverture_globale_pct: 99,
        couverture_hiver_pct: 100,
        trou_max_h: 3,
        meilleure_periode: null,
    },
    economie: {
        ferme_eur: -31,
        cmdps_estimee_eur: -26,
        nette_eur: -57,
        relative_pct: -0.5,
    },
    explain: {
        conclusion:
            "Pas de changement de FTA (BTSUPCU4 maintenu) : la structure actuelle est déjà quasi-optimale (surcoût net de 57 € si on suivait la reco).",
        pourquoi:
            "HPH peut descendre à 56 kVA mais HCH/HPB/HCB devraient monter à 65 kVA : l'arbitrage CSF↔CMDPS ne produit pas de gain net.",
        notes: "",
        generated: true,
    },
}

/** C5 — puissance souscrite unique (pas de dénivelé), économie simple. */
export const optimTurpeV2MockC5: OptimTurpeV2Etude = {
    _id: "mock-c5-0001",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-16T09:20:00.000Z",
    source: { idsource: "21001234567890" },
    compteur: {
        segment: "C5",
        raccordement: { puissance_raccordement_soutirage: 36 },
    },
    inputs: {
        source: { _id_source: "mock-c5-source" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTINFMU4" },
            puissance_souscrite_soutirage: { puissance: 30 },
        },
        couts: { turpe: 1240, turpeCSF: 720, turpeCSV: 520 },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTINFCU4" },
            puissance_souscrite_soutirage: { puissance: 18 },
        },
        couts: { turpe: 980, turpeCSF: 470, turpeCSV: 510 },
        variations: {
            variation_absolue_turpe: -260,
            variation_relative_turpe: -0.21,
        },
    },
    pointes_mesurees_kw: { HCB: 15 },
    // Comme le moteur le sert en C5 (`restitution.pmax_mesuree`) : la marge
    // appliquée d'office est une fraction (Q10).
    pmax_mesuree: { valeur: 15, unite: "kVA", marge_appliquee: 0.15 },
    qualite_donnees: {
        niveau: "fiable",
        couverture_globale_pct: 100,
        couverture_hiver_pct: 100,
        trou_max_h: 0,
        meilleure_periode: null,
    },
    economie: {
        ferme_eur: 260,
        cmdps_estimee_eur: 0,
        nette_eur: 260,
        relative_pct: 21,
    },
}

/**
 * C5 dont le pic dépasse la puissance souscrite (Q22), déjà optimal : la
 * réserve servie `c5-pic-au-dessus-de-la-souscrite` porte le titre de la
 * carte, et la barre collante écrit « Pic au-dessus de la souscrite » (revue
 * témoin du 15/10/2026, D1). Libellé et valeurs fictifs.
 */
export const optimTurpeV2MockC5ReservePic: OptimTurpeV2Etude = {
    ...optimTurpeV2MockC5,
    _id: "mock-c5-reserve-pic",
    actuelle: {
        ...optimTurpeV2MockC5.actuelle,
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTINFMU4" },
            puissance_souscrite_soutirage: { puissance: 3 },
        },
    },
    meilleure: {
        ...optimTurpeV2MockC5.meilleure,
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTINFMU4" },
            puissance_souscrite_soutirage: { puissance: 3 },
        },
    },
    pointes_mesurees_kw: { HCB: 4.49 },
    pmax_mesuree: { valeur: 4.49, unite: "kVA", marge_appliquee: 0.15 },
    economie: {
        ferme_eur: 0,
        cmdps_estimee_eur: 0,
        nette_eur: 0,
        relative_pct: 0,
        montant_servi: true,
    },
    etat: "deja_optimale",
    reserves: [
        {
            code: "c5-pic-au-dessus-de-la-souscrite",
            portee: "recommandation",
            label: "Le pic mesuré (4,49 kVA) dépasse la puissance souscrite (3 kVA) : le compteur peut disjoncter. Passer à 6 kVA coûterait 36 € HT de plus par an.",
            valeurs: {
                pic_kva: 4.49,
                souscrite_kva: 3,
                puissance_adaptee_kva: 6,
                surcout_annuel_eur: 36,
            },
        },
    ],
}

/** Index par id pour le store mock. */
export const optimTurpeV2MockEtudes: Record<string, OptimTurpeV2Etude> = {
    // Fixtures de la forme SERVIE par le moteur relu (DEV-1913), en tête pour
    // qu'elles soient les premières proposées par le sélecteur des stories.
    ...optimTurpeV2ServiEtudes,
    "mock-c4-0001": optimTurpeV2MockC4,
    "mock-c2-0002": optimTurpeV2MockC2,
    "mock-c2-0001": optimTurpeV2MockC2SurSegmente,
    "mock-c2-0003": optimTurpeV2MockC2EconomieSurDim,
    "mock-c4-0002": optimTurpeV2MockC4DejaOptimal,
    "mock-c5-0001": optimTurpeV2MockC5,
    "mock-c5-reserve-pic": optimTurpeV2MockC5ReservePic,
}
